"use client";

import type { InputSchema, ModelContextTool } from "@mcp-b/webmcp-types";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";

import { ensureWebMCP } from "@/lib/webmcp/runtime";

export type WebMCPTool = ModelContextTool<Record<string, unknown>, unknown> & {
  inputSchema: InputSchema;
};

function errorKind(error: unknown): string {
  if (error instanceof Error) return error.name || "Error";
  return "unknown";
}

export function useWebMCPTool(tool: WebMCPTool, enabled = true): void {
  const pathname = usePathname();
  const locale = useLocale();
  const posthog = usePostHog();
  const toolRef = useRef(tool);
  toolRef.current = tool;

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();

    async function registerTool() {
      const modelContext = await ensureWebMCP();
      if (!modelContext || controller.signal.aborted) return;

      const wrappedTool = {
        ...toolRef.current,
        async execute(input: Record<string, unknown>) {
          const startedAt = performance.now();
          try {
            const result = await toolRef.current.execute(input);
            posthog.capture("webmcp_tool_called", {
              tool: toolRef.current.name,
              route: pathname,
              locale,
              success: true,
              duration_ms: Math.round(performance.now() - startedAt),
              $set_once: {
                webmcp_first_used_at: new Date().toISOString(),
              },
            });
            return result;
          } catch (error) {
            posthog.capture("webmcp_tool_called", {
              tool: toolRef.current.name,
              route: pathname,
              locale,
              success: false,
              duration_ms: Math.round(performance.now() - startedAt),
              error_kind: errorKind(error),
              $set_once: {
                webmcp_first_used_at: new Date().toISOString(),
              },
            });
            throw error;
          }
        },
      };

      try {
        await modelContext.registerTool(wrappedTool, {
          signal: controller.signal,
        });
      } catch {
        // Runtime and registration failures must never affect the page.
      }
    }

    void registerTool();

    return () => {
      controller.abort();
    };
  }, [enabled, locale, pathname, posthog]);
}
